interface KaraokeConceptProps {
  lang: 'en' | 'ja';
}

export default function KaraokeConcept({ lang }: KaraokeConceptProps) {
  const content = {
    en: {
      badge: 'Our Concept',
      title: 'Like Singing with a Local Friend',
      description: 'Japanese karaoke can be confusing for first-timers. We\'re here to make it easy and fun. No pressure, no stress—just enjoy the music and the moment.',
      features: [
        {
          icon: 'ri-user-smile-line',
          title: 'Friendly Guide',
          text: 'We\'ll be right there with you, making sure everything goes smoothly.'
        },
        {
          icon: 'ri-music-2-line',
          title: 'Song Recommendations',
          text: 'Not sure what to sing? We\'ll suggest popular J-Pop, anime songs, and English hits.'
        },
        {
          icon: 'ri-chat-smile-3-line',
          title: 'Relaxed Atmosphere',
          text: 'No forced explanations. We\'ll answer questions only when you ask.'
        },
        {
          icon: 'ri-star-smile-line',
          title: 'Join the Fun',
          text: 'We\'ll sing along with you and help create unforgettable memories.'
        }
      ]
    },
    ja: {
      badge: 'コンセプト',
      title: '地元の友人と一緒に歌うように',
      description: '日本のカラオケは初めての方には戸惑うことも。私たちがすべてを簡単で楽しいものにします。プレッシャーもストレスもなく、音楽とその瞬間を楽しんでください。',
      features: [
        {
          icon: 'ri-user-smile-line',
          title: 'フレンドリーなガイド',
          text: 'すべてがスムーズに進むよう、私たちがそばにいます。'
        },
        {
          icon: 'ri-music-2-line',
          title: '曲のおすすめ',
          text: '何を歌えばいいかわからない？人気のJ-Pop、アニメソング、英語の曲をご提案します。'
        },
        {
          icon: 'ri-chat-smile-3-line',
          title: 'リラックスした雰囲気',
          text: '無理な説明はしません。質問があったときだけお答えします。'
        },
        {
          icon: 'ri-star-smile-line',
          title: '一緒に盛り上がる',
          text: '一緒に歌って、忘れられない思い出を作りましょう。'
        }
      ]
    }
  };

  const t = content[lang];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-pink-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full mb-6">
            <i className="ri-heart-3-line text-pink-600"></i>
            <span className="text-pink-700 text-sm font-semibold tracking-wide uppercase">{t.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t.description}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.map((feature, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-pink-100"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className={`${feature.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}